import React from "react"
import { Box, Grid, Divider, Fab } from "@mui/material"
import { makeStyles } from "makeStyles"
import Icon from "@mdi/react"
import { mdiDelete, mdiHeartOutline, mdiChevronRight, mdiChevronUp, mdiCartPlus } from "@mdi/js"

import Price from "components/Price"
import Link from "components/i18n/Link"
import Button from "components/Buttons/Button"
import ChangeAmountButton from "components/Buttons/ChangeAmountButton"

import useToggle from "@secureo/common/hooks/useToggle"

import getDeliveryTimeWorkDaysString from "@secureo/common/utils/commerceTools/formatters/getDeliveryTimeWorkDaysString"

import { MergedConfiguredLineItem } from "@secureo/common/typings/Cart"
import { CountryCode } from "@secureo/common/typings/CountryCode"

export const getUpdatedConfigurationQuantity = (
	prevConfiguredLineItemQuantity: number,
	newConfiguredLineItemQuantity: number,
	prevConfigurationQuantity: number,
) => {
	const configurationQuantityPerConfiguredLineItemQuantity =
		prevConfigurationQuantity / prevConfiguredLineItemQuantity
	const updatedConfigurationQuantity =
		newConfiguredLineItemQuantity * configurationQuantityPerConfiguredLineItemQuantity

	return updatedConfigurationQuantity
}

const useStyles = makeStyles()({
	responsiveItemButtons: {
		display: "flex",
	},
	price: {
		textAlign: "right",
	},
	desktopPrice: {
		display: "block",
		textAlign: "right",
		"@media only screen and (max-width: 580px)": {
			display: "none",
		},
	},
	mobilePrice: {
		display: "block",
		"@media only screen and (min-width: 580px)": {
			display: "none",
		},
	},
	deleteButton: {
		background: "none",
		border: "1px solid #C3423F",
		boxShadow: "none",
		color: "#C3423F",
		float: "right",
		marginLeft: 7,
		"&:hover": {
			background: "#C3423F",
			color: "white",
		},
	},
	wishlistAndCompareButton: {
		background: "none",
		border: "1px solid #4691CE",
		boxShadow: "none",
		color: "#4691CE",
		float: "right",
		marginLeft: 7,
		"&:hover": {
			background: "#4691CE",
			color: "white",
		},
	},
	changeAmountButton: {
		float: "right",
		marginLeft: 7,
	},
	"@media only screen and (max-width: 580px)": {
		deleteButton: { float: "left" },
		wishlistAndCompareButton: { float: "left" },
		changeAmountButton: { float: "right" },
		price: {
			textAlign: "left",
		},
	},
})

export const getConfigurationName = (name: string) => {
	if (name.includes("GSD")) {
		return "Gleichschliessend (GSD)"
	}

	if (name.includes("EZS")) {
		return "Einzelschliessend (EZS)"
	}

	return name
}

const Configuration = ({
	hasConfigurations,
	showConfigurations,
	toggleShowConfigurations,
	lineItemQuantity,
	configurations,
}: ConfigurationsProps) => {
	return (
		<>
			{hasConfigurations && (
				<div>
					<Button
						color="link-no-underline"
						onClick={toggleShowConfigurations}
						disableRipple
						style={{ paddingLeft: 0 }}
					>
						<Icon
							path={showConfigurations ? mdiChevronUp : mdiChevronRight}
							size={1}
							color="black"
						/>{" "}
						<strong>
							Konfigurationen {showConfigurations ? "verbergen" : "anzeigen"}
						</strong>
					</Button>

					<table style={{ width: "100%" }} cellPadding={5}>
						{showConfigurations &&
							configurations.map((configuration) => {
								const {
									id,
									name,
									quantity,
									taxedPrice: {
										totalGross: { centAmount: totalGrossCents },
									},
								} = configuration

								const configurationName = getConfigurationName(name)

								return (
									<tr
										key={id}
										style={{
											borderBottom: "1px solid whitesmoke",
											fontWeight: 300,
										}}
									>
										<td style={{ width: 30 }}>
											{quantity / lineItemQuantity}x
										</td>
										<td style={{ textAlign: "left" }}>{configurationName}</td>
										<td style={{ textAlign: "right" }}>
											<Price priceInEuroCents={totalGrossCents} />
										</td>
									</tr>
								)
							})}
					</table>
				</div>
			)}
		</>
	)
}

const LineItem = ({
	lineItem,
	shopCountryCode,
	isLoading,
	changeQuantity,
	addToShoppingList,
	addToCart,
	remove,
}: Props) => {
	const { classes } = useStyles()
	const [showConfigurations, toggleShowConfigurations] = useToggle(false)

	const {
		quantity: lineItemQuantity,
		variant: { sku, images = [], deliveryTimeWorkDaysByShopCountryCode },
		taxedPrice: {
			totalGross: { centAmount: totalGrossCents },
		},
		configurations = [],
		productSlug,
	} = lineItem

	const name = lineItem.variant?.variantName || lineItem.name
	const img = images[0] || "https://via.placeholder.com/100x100"
	const hasConfigurations = (configurations?.length ?? 0) > 0
	const deliveryTimeWorkDays = deliveryTimeWorkDaysByShopCountryCode?.[shopCountryCode] ?? 0

	return (
		<Box mb={2}>
			<Grid container spacing={3}>
				<Grid item xs={3} sm={2}>
					<img
						src={img}
						alt={name}
						style={{
							width: "100%",
							maxHeight: 120,
							paddingTop: 15,
							objectFit: "contain",
						}}
					/>
				</Grid>
				<Grid item xs={9} sm={8}>
					<Link
						href={`/p/${productSlug}`}
						style={{ textDecoration: "none", color: "inherit" }}
					>
						<div className="h2">
							{lineItemQuantity}x {name}
						</div>
					</Link>
					<Box style={{ color: "#505e5f", fontSize: "0.75rem" }}>ArtikelNr.: {sku}</Box>
					{deliveryTimeWorkDays > 0 && (
						<Box mt={0} style={{ color: "#5DA67A", fontSize: "0.75rem" }}>
							<strong>
								Lieferzeit: {getDeliveryTimeWorkDaysString(deliveryTimeWorkDays)}
							</strong>
						</Box>
					)}
					<Box mt={1} className={classes.mobilePrice}>
						<strong>
							<Price priceInEuroCents={totalGrossCents} />
						</strong>
					</Box>
				</Grid>

				<Grid item xs={12} sm={2} className={classes.desktopPrice}>
					<strong>
						<Price priceInEuroCents={totalGrossCents} />
					</strong>
				</Grid>
				<Grid container spacing={3}>
					<Grid item xs={3} sm={2} />
					<Grid item xs={12} sm={10}>
						<Configuration
							hasConfigurations={hasConfigurations}
							showConfigurations={showConfigurations}
							toggleShowConfigurations={toggleShowConfigurations}
							lineItemQuantity={lineItemQuantity}
							configurations={configurations}
						/>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<div className={classes.changeAmountButton}>
						<ChangeAmountButton
							quantity={lineItemQuantity}
							onChange={(newConfiguredLineItemQuantity) =>
								changeQuantity(lineItem, newConfiguredLineItemQuantity)
							}
							minQuantity={1}
							disabled={isLoading}
						/>
					</div>

					{addToShoppingList && (
						<Fab
							color="secondary"
							aria-label="add"
							size="small"
							className={classes.wishlistAndCompareButton}
							onClick={() => {
								addToShoppingList(lineItem)
							}}
							disabled={isLoading}
						>
							<Icon path={mdiHeartOutline} size={1} />
						</Fab>
					)}

					{addToCart && (
						<Fab
							color="secondary"
							aria-label="add"
							size="small"
							className={classes.wishlistAndCompareButton}
							onClick={() => {
								addToCart(lineItem)
							}}
							disabled={isLoading}
						>
							<Icon path={mdiCartPlus} size={1} />
						</Fab>
					)}

					<Fab
						color="primary"
						aria-label="remove lineItem from cart"
						onClick={() => {
							remove(lineItem)
						}}
						disabled={isLoading}
						size="small"
						className={classes.deleteButton}
					>
						<Icon path={mdiDelete} size={1} />
					</Fab>
				</Grid>
			</Grid>
			<Box mt={2}>
				<Divider />
			</Box>
		</Box>
	)
}

interface Props {
	lineItem: MergedConfiguredLineItem
	shopCountryCode: CountryCode
	isLoading: boolean
	changeQuantity: (
		lineItem: MergedConfiguredLineItem,
		newConfiguredLineItemQuantity: number,
	) => Promise<void>
	addToCart?: (lineItem: MergedConfiguredLineItem) => Promise<void>
	addToShoppingList?: (lineItem: MergedConfiguredLineItem) => Promise<void>
	remove: (lineItem: MergedConfiguredLineItem) => void
}

interface ConfigurationsProps {
	hasConfigurations: boolean
	showConfigurations: boolean
	toggleShowConfigurations: () => void
	lineItemQuantity: number
	configurations: any
}

export default LineItem
